import { useStanQuery } from '.';
import { fetchCurrencies } from '@/lib/api/income/api';
import { CurrencyInfo } from '@/lib/api/income/types';
import { QUERY_KEYS } from '@/lib';

export interface Currency {
    name: string;
    value: string;
    symbol: string;
}

/**
 * Transform API response object to array format
 * Matches Vue's currencyList format: shows symbol when it differs from code
 * e.g., "Canadian Dollar (CAD, C$)" vs "US Dollar (USD)"
 */
const transformCurrencies = (data: Record<string, CurrencyInfo>): Currency[] => {
    return Object.entries(data).map(([code, info]) => ({
        name: code === info.symbol ? `${info.name} (${code})` : `${info.name} (${code}, ${info.symbol})`,
        value: code,
        symbol: info.symbol,
    }));
};

// Fallback currencies matching Vue's default (USD only)
const FALLBACK_CURRENCIES: Currency[] = [{ name: 'US Dollar (USD)', value: 'USD', symbol: '$' }];

/**
 * Hook to fetch available currencies
 * Data is cached for 30 minutes as currencies rarely change
 */
export function useCurrencies() {
    const {
        data: rawData,
        isLoading,
        error,
        refetch,
    } = useStanQuery<Record<string, CurrencyInfo>>({
        queryKey: [QUERY_KEYS.CURRENCIES],
        queryFn: fetchCurrencies,
        staleTime: 1000 * 60 * 30, // 30 minutes - currencies don't change often
        meta: {
            error: {
                sentryMessage: '[CURRENCIES] failed-to-fetch-currencies',
                message: 'Failed to load currencies. Using default options.',
            },
        },
    });

    // Transform object to array, fallback to defaults if no data
    const currencies = rawData ? transformCurrencies(rawData) : FALLBACK_CURRENCIES;

    return {
        currencies,
        isLoading,
        error,
        refetch,
    };
}
